import { Alert, Box, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, Link, Snackbar, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  authContainerStyling,
  authHeaderContainerStyling,
  authHeaderTypographyStyling,
  authLogoStyling,
  authSecondaryContainerStyling,
  createAccountContainerStyling,
  externalFormStyling,
} from '../styles/sxStyling';
import _ from 'lodash';
import { EMAIL_REGEX } from '../utility/inputUtils';
import CustomAxios from '../utility/customAxios';

const GRECAPTCHA_CLIENT_ID = process.env.REACT_APP_GRECAPTCHA_CLIENT_ID;

interface CreateAccountState {
  org_name: string;
  org_email: string;
  org_phone: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

const BLANK_STATE = {
  org_name: '',
  org_email: '',
  org_phone: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
};

const REQUIRED_FIELDS: (keyof CreateAccountState)[] = ['org_name', 'org_email', 'first_name', 'last_name', 'email'];

function SignUpModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Welcome</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography>Thank you for signing up for Schedulink.io! We&apos;re excited to have you on board.</Typography>
        <Typography>
          You should get an email from us in the next few minutes to verify your address. Just click the link in that email to finish setting up your account
          and get logged in.
        </Typography>
        <Typography>
          If you hit any snags or have questions, don&apos;t hesitate to check out our help center or contact our support team. We&apos;re here to help!
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CreateAccount() {
  const [formState, setFormState] = useState<CreateAccountState>(BLANK_STATE);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [grecaptchaResponse, setGrecaptchaResponse] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const updateFormData = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!_.includes(_.keys(BLANK_STATE), e.target.id)) return;
    setFormState({ ...formState, [e.target.id]: e.target.value });
  };

  const isValid = useMemo(
    () =>
      _.every(_.at(formState, REQUIRED_FIELDS), (v) => !!_.trim(v)) &&
      EMAIL_REGEX.test(formState.org_email) &&
      EMAIL_REGEX.test(formState.email) &&
      grecaptchaResponse,
    [formState, grecaptchaResponse]
  );

  const onSubmit = () => {
    CustomAxios.post('organizations/create-account', { ...formState, captcha_id: grecaptchaResponse })
      .then(() => setModalOpen(true))
      .catch((error) => {
        const response = error.response;
        if (response?.status === 400) {
          setError(response.data.error);
        } else {
          setError('Unable to create the account. Please contact support.');
        }
      });
  };

  const onGrecaptchLoad = () => {
    // @ts-expect-error: Grecaptcha loaded at runtime - no typing
    const grecaptcha: any = window.grecaptcha;
    if (!grecaptcha) return;
    grecaptcha.render('grecaptcha-container', {
      sitekey: GRECAPTCHA_CLIENT_ID,
      callback: setGrecaptchaResponse,
      'expired-callback': () => setGrecaptchaResponse(null),
    });
  };

  useEffect(() => {
    // @ts-expect-error: Assigning component inner function to global scope
    window.onGrecaptchLoad = onGrecaptchLoad;
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onGrecaptchLoad&render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  return (
    <Container component="main" maxWidth="md" sx={authContainerStyling}>
      <title>ScheduLink | Studio Management Software for Volume Photo Studios</title>
      <Box sx={authSecondaryContainerStyling}>
        <Box sx={{ ...authHeaderContainerStyling, width: { xs: '100%', md: '50%' } }}>
          <Stack sx={{ gap: { xs: 1, md: 5 } }}>
            <Typography variant="h1" sx={authHeaderTypographyStyling}>
              Create your ScheduLink Account
            </Typography>
            <Typography color="white">
              This page is for new organisations. If your team already uses ScheduLink, hop over to your invite email to log in.
            </Typography>
          </Stack>
          <Box component="img" src="/svg/schedulink-logo.svg" alt="logo" sx={authLogoStyling} />
        </Box>
        <Box sx={createAccountContainerStyling}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 4, width: '100%' }}>
            <Box sx={{ ...externalFormStyling, gap: 5, width: '100%' }}>
              <Typography variant="h2" sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 500, whiteSpace: 'nowrap' }}>
                About your organization
              </Typography>
              <TextField autoFocus required label="Organization Name" id="org_name" value={formState.org_name} onChange={updateFormData} />
              <TextField required label="Organization Email" id="org_email" value={formState.org_email} onChange={updateFormData} />
              <TextField label="Phone (Optional)" id="org_phone" value={formState.org_phone} onChange={updateFormData} />
            </Box>
            <Box sx={{ ...externalFormStyling, gap: 5, width: '100%' }}>
              <Typography variant="h2" sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 500 }}>
                About you
              </Typography>
              <TextField required label="First Name" id="first_name" value={formState.first_name} onChange={updateFormData} />
              <TextField required label="Last Name" id="last_name" value={formState.last_name} onChange={updateFormData} />
              <TextField required label="Your Email" id="email" value={formState.email} onChange={updateFormData} />
            </Box>
          </Box>

          <Box sx={{ ...externalFormStyling, width: '100%' }}>
            <Box id="grecaptcha-container" sx={{ display: 'flex', justifyContent: 'center' }} />
            <Typography variant="body2" sx={{ maxWidth: '400px' }}>
              By signing up, you agree to Schedulink&apos;s{' '}
              <Link target="_blank" href="https://schedulink.io/terms-of-service">
                Terms of Service
              </Link>{' '}
              and acknowledge the Schedulink{' '}
              <Link target="_blank" href="https://schedulink.io/privacy-policy">
                Privacy Statement
              </Link>
              .
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <Button size="medium" variant="contained" onClick={onSubmit} disabled={!isValid}>
                Create Account
              </Button>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>Already have an account?</Typography>
                <Link href="/login">login</Link>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <SignUpModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <Snackbar open={!!error} onClose={() => setError(null)} message={error}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
}
